/**
 * failed-nodes — o que a tela do histórico precisa para mostrar UMA falha com
 * clareza (spec 2026-09-20 §2.3, D20): a lista dos nós que falharam, lida de
 * `runData`, e a régua que decide se o texto do erro precisa de "ver mais".
 *
 * Puro, sem React — testado em vitest no ambiente `node` (o front não tem
 * testing-library; componente é provado no Playwright).
 */
import type { WorkflowExecutionResponse } from '@troia-v3/types';

export interface FailedNodeLine {
  nodeId: string;
  label: string;
  error: string;
}

/**
 * Nó que falhou mas cujo id NÃO está no desenho atual. Duas causas
 * indistinguíveis entre si: (1) o nó foi apagado depois desta execução, OU
 * (2) a execução foi gravada ANTES da Fase 1 (spec 2026-09-20) e o id é um
 * identificador INTERNO do motor (embrulho do compilador — `sub-<nó>-true`,
 * `-fan-0`, `-retry-guard`, `input`) que a captura antiga não sabia filtrar
 * (ver "Só nó de verdade entra no histórico (I16)" em `WORKFLOWS.md`).
 * Farejar o prefixo `sub-` decidiria por string, o que a regra I16 proíbe —
 * por isso o rótulo fica NEUTRO em vez de afirmar "removido" quando pode não
 * ter sido. Nunca esconder a linha: em execução antiga que acordou dentro de
 * um ramo, o embrulho é o ÚNICO registro da falha.
 */
export const UNKNOWN_NODE_LABEL = 'Nó não identificado';
/** Nó que existe no desenho mas está sem rótulo. */
const UNNAMED_NODE_LABEL = 'Nó sem nome';
/** Entrada `failed` sem texto de erro (ou só com espaços). */
const FAILED_WITHOUT_MESSAGE = 'O nó falhou e não informou o motivo.';

/**
 * Largura útil do PIOR caso em caracteres: folha mobile de 320px, `text-xs`
 * (~6,2px por caractere) → 254px ÷ 6,2 ≈ 40. Conservador de propósito: botão
 * "ver mais" sobrando é inofensivo; texto cortado sem saída é o defeito.
 */
const CHARS_PER_LINE = 40;

/**
 * Nós cuja ÚLTIMA entrada em `runData` terminou `failed`, na ordem em que
 * falharam. Falha que um "Tentar de novo" resolveu (última entrada
 * `completed`) não entra. O rótulo vem do desenho ATUAL: nó renomeado mostra
 * o nome novo; nó cujo id não está no desenho vira `UNKNOWN_NODE_LABEL`.
 */
export function listFailedNodes(
  runData: WorkflowExecutionResponse['runData'] | undefined,
  nodes: ReadonlyArray<{ id: string; data?: { label?: string } }>,
): FailedNodeLine[] {
  if (!runData) return [];

  const labelById = new Map<string, string | undefined>();
  for (const node of nodes) labelById.set(node.id, node.data?.label);

  const found: Array<FailedNodeLine & { startedAt: string }> = [];
  for (const [nodeId, entries] of Object.entries(runData)) {
    if (!Array.isArray(entries) || entries.length === 0) continue;
    const last = entries[entries.length - 1];
    if (last?.status !== 'failed') continue;

    const rawLabel = labelById.get(nodeId);
    const label = !labelById.has(nodeId)
      ? UNKNOWN_NODE_LABEL
      : typeof rawLabel === 'string' && rawLabel.trim() !== ''
        ? rawLabel
        : UNNAMED_NODE_LABEL;
    const error = typeof last.error === 'string' && last.error.trim() !== '' ? last.error : FAILED_WITHOUT_MESSAGE;

    found.push({ nodeId, label, error, startedAt: typeof last.startedAt === 'string' ? last.startedAt : '' });
  }

  // ISO 8601 ordena como texto; empate mantém a ordem das chaves (sort estável).
  found.sort((x, y) => (x.startedAt < y.startedAt ? -1 : x.startedAt > y.startedAt ? 1 : 0));
  return found.map((line) => ({ nodeId: line.nodeId, label: line.label, error: line.error }));
}

/**
 * O texto passa de `maxLines` linhas quando desenhado no card do histórico?
 * Conta quebras de linha reais + a dobra de linhas compridas. Sem medir layout
 * (a lista tem até 50 cards e refaz a cada 10 s).
 */
export function isLongErrorText(text: string, maxLines = 4, charsPerLine = CHARS_PER_LINE): boolean {
  let lines = 0;
  for (const line of text.split('\n')) {
    lines += Math.max(1, Math.ceil(line.length / charsPerLine));
    if (lines > maxLines) return true;
  }
  return false;
}
